'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard, ShoppingBag, Truck, Users, Package,
  DollarSign, BarChart3, ChevronLeft, ChevronRight,
  ChefHat, Bell, Settings, LogOut, MapPin, ClipboardList, Briefcase, UserCheck
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href: string;
  badge?: number;
  group: string;
}

const navItems: NavItem[] = [
  { id: 'nav-dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} />, href: '/', group: 'Operations' },
  { id: 'nav-admin', label: 'Admin Dashboard', icon: <LayoutDashboard size={18} />, href: '/admin-dashboard', group: 'Operations' },
  { id: 'nav-manager', label: 'Manager Dashboard', icon: <Briefcase size={18} />, href: '/manager-dashboard', group: 'Operations' },
  { id: 'nav-driver', label: 'Driver Dashboard', icon: <UserCheck size={18} />, href: '/driver-dashboard', group: 'Operations' },
  { id: 'nav-production', label: 'Production', icon: <ChefHat size={18} />, href: '/admin-dashboard', group: 'Operations' },
  { id: 'nav-orders', label: 'Orders', icon: <ShoppingBag size={18} />, href: '/admin-dashboard', badge: 7, group: 'Operations' },
  { id: 'nav-deliveries', label: 'Deliveries', icon: <Truck size={18} />, href: '/admin-dashboard', badge: 3, group: 'Operations' },
  { id: 'nav-routes', label: 'Routes', icon: <MapPin size={18} />, href: '/admin-dashboard', group: 'Operations' },
  { id: 'nav-drivers', label: 'Drivers', icon: <Users size={18} />, href: '/admin-dashboard', group: 'People' },
  { id: 'nav-customers', label: 'Customers', icon: <Users size={18} />, href: '/admin-dashboard', group: 'People' },
  { id: 'nav-inventory', label: 'Inventory', icon: <Package size={18} />, href: '/admin-dashboard', badge: 4, group: 'Inventory' },
  { id: 'nav-suppliers', label: 'Suppliers', icon: <ClipboardList size={18} />, href: '/admin-dashboard', group: 'Inventory' },
  { id: 'nav-cash', label: 'Cash & Payments', icon: <DollarSign size={18} />, href: '/admin-dashboard', group: 'Finance' },
  { id: 'nav-reports', label: 'Reports', icon: <BarChart3 size={18} />, href: '/admin-dashboard', group: 'Finance' },
  { id: 'nav-notifications', label: 'Notifications', icon: <Bell size={18} />, href: '/admin-dashboard', badge: 2, group: 'System' },
  { id: 'nav-settings', label: 'Settings', icon: <Settings size={18} />, href: '/admin-dashboard', group: 'System' },
];

const groups = ['Operations', 'People', 'Inventory', 'Finance', 'System'];

export default function AdminSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  return (
    <aside
      className={`relative flex flex-col h-screen bg-card border-r border-border transition-all duration-300 ease-in-out flex-shrink-0 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-4 border-b border-border ${collapsed ? 'justify-center' : ''}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <div className="overflow-hidden">
            <span className="font-bold text-base text-foreground leading-tight block">HummusHub</span>
            <span className="text-xs text-muted-foreground">Operations</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2">
        {groups.map(group => {
          const groupItems = navItems.filter(i => i.group === group);
          return (
            <div key={`group-${group}`} className="mb-4">
              {!collapsed && (
                <p className="text-[10px] font-700 uppercase tracking-widest text-muted-foreground px-2 mb-1">
                  {group}
                </p>
              )}
              {groupItems.map(item => {
                const isActive = pathname === item.href;
                return (
                  <Link
                    key={item.id}
                    href={item.href}
                    title={collapsed ? item.label : undefined}
                    className={`nav-item mb-0.5 ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`}
                  >
                    <span className="flex-shrink-0">{item.icon}</span>
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.badge && (
                          <span className="ml-auto text-xs font-700 bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 min-w-[20px] text-center leading-none">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                    {collapsed && item.badge && (
                      <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-primary rounded-full" />
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* User section */}
      <div className={`border-t border-border p-3 ${collapsed ? 'flex justify-center' : ''}`}>
        {!collapsed ? (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-700 flex-shrink-0">
              MA
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-600 text-foreground truncate">Marcus Antoine</p>
              <p className="text-xs text-muted-foreground">Owner</p>
            </div>
            <button className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-negative transition-colors" title="Sign out">
              <LogOut size={15} />
            </button>
          </div>
        ) : (
          <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-700">
            MA
          </div>
        )}
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(c => !c)}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 z-10"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}